import { isTag } from 'anodum';
import bowser from 'yola-bowser';
import getAdvancedSelection from '../../text-formatting/accessors/get-advanced-selection';
import splitElementByCollapsedSelection from '../helpers/split-element-by-collapsed-selection';
import setCaretAtTheBeginningOfNode from '../helpers/set-caret-at-the-beginning-of-node';
import insertBreakIntoEmptyEditableElement from '../helpers/insert-break-into-empty-editable-element';
import isEmptyEditableElement from '../verifiers/is-empty-editable-element';
import handleAdjacentNode from '../helpers/handle-adjacent-node';
import insertBreakIntoEditableElement from '../helpers/insert-break-into-editable-element';
import handleLastEmptyListItemBySelection from '../helpers/handle-empty-last-list-item';
import getCollapsedNodeAndOffset from '../helpers/get-collapsed-node-and-offset';
import findRootEditableElement from '../helpers/find-root-editable-element';
import canBeRichEdited from '../../constraints/verifiers/can-be-rich-edited';
import insertBreakOnReturn from '../../hdrm/utils/helpers/insert-break-on-return';
import cleanBreakElements from '../helpers/clean-break-elements';
import removeSelection from '../helpers/remove-selection';
import clearTextNodes from '../helpers/clear-text-nodes';
import { ENTER_KEY } from '../constants/keys';
import { LIST_ITEM } from '../constants/editable-elements';
import isBreakElement from '../verifiers/is-break-element';
import setCaretInTheEndOfNode from '../helpers/set-caret-in-the-end-of-node';
import removeEmptyLinksFromListItem from '../helpers/remove-empty-links-from-list-item';
import removeEmptyDecorations from '../helpers/remove-empty-decorations';

const returnHandler = (e) => {
  if (e.key !== ENTER_KEY) return;

  const closestEditableElement = e.srcElement.closest('[contenteditable]');
  const selection = getAdvancedSelection();
  const { isCollapsed } = selection;
  let { anchorNode } = selection;
  if (isCollapsed) {
    [anchorNode] = getCollapsedNodeAndOffset();
  }
  const anchorContainer = findRootEditableElement(anchorNode);
  const isBrLastChildAnchorNode =
    isBreakElement(anchorNode) && isBreakElement(anchorNode.parentElement.lastChild);
  if (!canBeRichEdited(closestEditableElement) || !anchorContainer) {
    insertBreakOnReturn(e);
    return;
  }

  e.preventDefault();

  cleanBreakElements(closestEditableElement);

  if (isCollapsed) {
    if (isBrLastChildAnchorNode) {
      setCaretInTheEndOfNode(anchorContainer);
    }

    // on ios devices `shiftKey` somethimes is `true` when shift is not pressed and vise versa
    if (e.shiftKey && !bowser.ios && !bowser.iPadOS) {
      insertBreakIntoEditableElement();
      return;
    }

    if (
      isTag(anchorContainer, LIST_ITEM) &&
      isEmptyEditableElement(anchorContainer) &&
      anchorContainer.parentNode.lastElementChild === anchorContainer
    ) {
      handleLastEmptyListItemBySelection(anchorContainer);
      return;
    }

    splitElementByCollapsedSelection(anchorContainer, true);
    return;
  }

  const [leftContainer, rightContainer] = handleAdjacentNode();

  removeSelection();

  const [node] = getCollapsedNodeAndOffset();

  if ((!leftContainer && !rightContainer) || leftContainer === rightContainer) {
    // Selection was inside of one editable element
    const editableElement = findRootEditableElement(node);
    removeEmptyLinksFromListItem(editableElement);
    removeEmptyDecorations(editableElement);

    if (e.shiftKey && !bowser.ios && !bowser.iPadOS) {
      insertBreakIntoEditableElement();
      return;
    }

    if (!editableElement.innerText) {
      insertBreakIntoEmptyEditableElement(editableElement);
    }

    splitElementByCollapsedSelection(editableElement);
    return;
  }

  if (leftContainer.innerText) {
    const newElement = leftContainer.cloneNode(true);

    clearTextNodes(newElement);
    removeEmptyLinksFromListItem(newElement);
    removeEmptyDecorations(newElement);
    insertBreakIntoEmptyEditableElement(newElement);

    leftContainer.insertAdjacentElement('afterend', newElement);
    setCaretAtTheBeginningOfNode(newElement);
    rightContainer.remove();
  } else {
    const newElement = rightContainer.cloneNode(true);

    clearTextNodes(newElement);
    removeEmptyDecorations(newElement);
    insertBreakIntoEmptyEditableElement(newElement);

    rightContainer.insertAdjacentElement('beforebegin', newElement);
    leftContainer.remove();
    if (isEmptyEditableElement(rightContainer)) {
      const newRightContainer = newElement.cloneNode(true);
      rightContainer.parentNode.replaceChild(newRightContainer, rightContainer);
      setCaretAtTheBeginningOfNode(newRightContainer);
      return;
    }
    setCaretAtTheBeginningOfNode(rightContainer);
  }
};

export default returnHandler;
